import { formatDate, parseISO } from 'date-fns';

import type {
  CommonFiltersAppState,
  MultiSelectFilterModifier,
  MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { MULTI_SELECT_FILTER_MODIFIERS } from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { guardMultiSelectFilter } from '@iconik-io/shared-lib/src/utils/search/guard_multi_select_filter';

import {
  guardFilterRange,
  guardFilterString,
} from '../../utils/search_utils/guard_filter';
import {
  getAvailableModifierOptions,
  getModifierDisplayLabel,
  normalizeModifier,
} from '../../utils/search_utils/modifier_options';
import { getStorageAvailableModifiers } from '../../utils/search_utils/storage_modifiers';

export type FilterChipEditorKind =
  | 'multiSelect'
  | 'dateRange'
  | 'facetChecklist'
  | 'boolean'
  | 'query'
  | 'text'
  | 'none';

/**
 * Sentinel key used to represent the free-text search query as a chip and to
 * pause it via `disabledFilters` (it is not a real `filterValues` key).
 */
export const SEARCH_QUERY_FILTER_KEY = '__search_query__';

/**
 * A single active-filter chip, derived synchronously from Redux search state.
 * A `MultiSelectFilterValue` with multiple groups produces one descriptor per
 * group (each group is its own chip, matching the design).
 */
export type ActiveFilterChipDescriptor = {
  /** Stable identity for React keys and per-chip editor open-state. */
  id: string;
  /** Key into `filterValues` (source 'filter') or `facetValues` (source 'facet'); the query sentinel for source 'query'. */
  stateKey: string;
  source: 'filter' | 'facet' | 'query';
  /** Which group of a multi-select filter this chip represents. */
  groupIndex?: number;
  fieldLabel: string;
  valueLabel: string;
  modifierLabel?: string;
  modifier?: MultiSelectFilterModifier;
  /** Modifier select options (value + display label), for inline editing. */
  modifierOptions?: { value: MultiSelectFilterModifier; label: string }[];
  editorKind: FilterChipEditorKind;
  isModifierEditable: boolean;
  disabled: boolean;
};

export type ActiveFilterChipsContext = {
  dateFormat: string;
  getFacetFieldLabel: (name: string) => string;
  formatFacetValue: (name: string, bucketKey: string) => string;
  getMetadataFieldLabel?: (fieldName: string) => string | undefined;
  /**
   * The modifiers a metadata field's filter offers, so the chip's modifier
   * dropdown matches the panel (e.g. a single-select drop_down is `*_any_of`
   * only). Returning `undefined` means the full modifier set.
   */
  getMetadataFieldModifiers?: (
    fieldName: string,
  ) => MultiSelectFilterModifier[] | undefined;
};

// Internal/default filter keys that never surface as user-facing chips.
const EXCLUDED_KEYS = new Set([
  'status',
  'in_collections',
  'ancestor_collections',
  'id',
  'favoured_by',
]);

type ItemNoun = { one: string; many: string };

type KnownFilterConfig = {
  fieldLabel: string;
  editorKind: Extract<FilterChipEditorKind, 'multiSelect' | 'dateRange'>;
  /** How to summarize a multi-select value: item count, or the items joined inline. */
  valueMode?: 'count' | 'items';
  itemNoun?: ItemNoun;
  availableModifiers?: MultiSelectFilterModifier[];
};

const KNOWN_FILTERS: Record<string, KnownFilterConfig> = {
  date_created: { fieldLabel: 'Date Created', editorKind: 'dateRange' },
  date_modified: { fieldLabel: 'Date Modified', editorKind: 'dateRange' },
  created_by_user: {
    fieldLabel: 'Created by',
    editorKind: 'multiSelect',
    valueMode: 'count',
    itemNoun: { one: 'user', many: 'users' },
    availableModifiers: ['include_any_of', 'exclude_any_of'],
  },
  storage_id: {
    fieldLabel: 'Storage',
    editorKind: 'multiSelect',
    valueMode: 'count',
    itemNoun: { one: 'storage', many: 'storages' },
  },
  person_ids: {
    fieldLabel: 'Confirmed Persons',
    editorKind: 'multiSelect',
    valueMode: 'count',
    itemNoun: { one: 'person', many: 'persons' },
  },
  transcription_text: {
    fieldLabel: 'Transcription Text',
    editorKind: 'multiSelect',
    valueMode: 'items',
  },
};

const METADATA_ITEM_NOUN: ItemNoun = { one: 'value', many: 'values' };

function pluralize(count: number, noun: ItemNoun): string {
  return `${count} ${count === 1 ? noun.one : noun.many}`;
}

/**
 * Returns a new multi-select value with `groupIndex`'s items replaced. Groups
 * left with no items are dropped, so clearing a chip's value removes it.
 */
export function setMultiSelectGroupItems(
  groups: MultiSelectFilterValue,
  groupIndex: number,
  items: string[],
): MultiSelectFilterValue {
  return groups
    .map((group, index) => (index === groupIndex ? { ...group, items } : group))
    .filter((group) => group.items.length > 0);
}

function formatRangeValue(
  range: { min?: string; max?: string },
  dateFormat: string,
): string {
  const format = (value?: string): string | undefined => {
    if (!value) {
      return undefined;
    }
    try {
      return formatDate(parseISO(value), dateFormat);
    } catch {
      return value;
    }
  };
  const min = format(range.min);
  const max = format(range.max);
  if (min && max) {
    return `${min} – ${max}`;
  }
  if (min) {
    return `after ${min}`;
  }
  if (max) {
    return `before ${max}`;
  }
  return '';
}

function multiSelectValueLabel(
  items: string[],
  config: Pick<KnownFilterConfig, 'valueMode' | 'itemNoun'>,
  context: ActiveFilterChipsContext,
  stateKey: string,
): string {
  if (config.valueMode === 'items') {
    return items
      .map((item) => context.formatFacetValue(stateKey, item))
      .join(', ');
  }
  return pluralize(items.length, config.itemNoun ?? METADATA_ITEM_NOUN);
}

function pushMultiSelectChips(
  chips: ActiveFilterChipDescriptor[],
  stateKey: string,
  value: unknown,
  config: Pick<
    KnownFilterConfig,
    'fieldLabel' | 'valueMode' | 'itemNoun' | 'availableModifiers'
  >,
  context: ActiveFilterChipsContext,
  disabled: boolean,
): boolean {
  const groups = guardMultiSelectFilter(value);
  if (!groups) {
    return false;
  }
  const availableModifiers =
    config.availableModifiers ?? MULTI_SELECT_FILTER_MODIFIERS;
  groups.forEach((group, groupIndex) => {
    if (group.items.length === 0) {
      return;
    }
    // Normalize the stored modifier onto an offered option so the chip's label
    // and the dropdown selection agree even when the field clamps its set.
    const modifier = normalizeModifier(
      group.modifier,
      availableModifiers,
      group.items.length,
    );
    chips.push({
      id: `${stateKey}#${groupIndex}`,
      stateKey,
      source: 'filter',
      groupIndex,
      fieldLabel: config.fieldLabel,
      valueLabel: multiSelectValueLabel(group.items, config, context, stateKey),
      modifier,
      modifierLabel: getModifierDisplayLabel(modifier, group.items.length),
      modifierOptions: getAvailableModifierOptions(
        availableModifiers,
        group.items.length,
      ),
      editorKind: 'multiSelect',
      isModifierEditable: true,
      disabled,
    });
  });
  return true;
}

/**
 * Produces the ordered list of active-filter chip descriptors from the search
 * filter state. Pure and synchronous — label resolution that needs the API
 * (real names for ID-based filters) is intentionally summarized as a count.
 */
export function getActiveFilterChips(
  searchFilters: Pick<
    CommonFiltersAppState,
    'filterValues' | 'facetValues' | 'disabledFilters'
  > & { query?: string },
  context: ActiveFilterChipsContext,
): ActiveFilterChipDescriptor[] {
  const disabledFilters = new Set(searchFilters.disabledFilters ?? []);
  const chips: ActiveFilterChipDescriptor[] = [];

  const query = searchFilters.query?.trim();
  if (query) {
    chips.push({
      id: 'query',
      stateKey: SEARCH_QUERY_FILTER_KEY,
      source: 'query',
      fieldLabel: 'Include',
      valueLabel: `'${query}'`,
      editorKind: 'query',
      isModifierEditable: false,
      disabled: disabledFilters.has(SEARCH_QUERY_FILTER_KEY),
    });
  }

  for (const [stateKey, value] of Object.entries(searchFilters.filterValues)) {
    if (EXCLUDED_KEYS.has(stateKey) || value == null) {
      continue;
    }
    const disabled = disabledFilters.has(stateKey);
    const known = KNOWN_FILTERS[stateKey];

    if (known?.editorKind === 'dateRange') {
      const range = guardFilterRange(value);
      if (range && (range.min || range.max)) {
        chips.push({
          id: stateKey,
          stateKey,
          source: 'filter',
          fieldLabel: known.fieldLabel,
          valueLabel: formatRangeValue(range, context.dateFormat),
          editorKind: 'dateRange',
          isModifierEditable: false,
          disabled,
        });
      }
      continue;
    }

    if (known?.editorKind === 'multiSelect') {
      const config =
        stateKey === 'storage_id'
          ? {
              ...known,
              availableModifiers: getStorageAvailableModifiers(
                value,
                searchFilters.facetValues.object_type,
              ),
            }
          : known;
      if (
        pushMultiSelectChips(chips, stateKey, value, config, context, disabled)
      ) {
        continue;
      }
    }

    if (stateKey.startsWith('metadata.')) {
      const fieldName = stateKey.replace('metadata.', '');
      const fieldLabel =
        context.getMetadataFieldLabel?.(fieldName) ?? fieldName;

      if (
        pushMultiSelectChips(
          chips,
          stateKey,
          value,
          {
            fieldLabel,
            valueMode: 'count',
            itemNoun: METADATA_ITEM_NOUN,
            availableModifiers: context.getMetadataFieldModifiers?.(fieldName),
          },
          context,
          disabled,
        )
      ) {
        continue;
      }
      const range = guardFilterRange(value);
      if (range && (range.min || range.max)) {
        chips.push({
          id: stateKey,
          stateKey,
          source: 'filter',
          fieldLabel,
          valueLabel: formatRangeValue(range, context.dateFormat),
          editorKind: 'dateRange',
          isModifierEditable: false,
          disabled,
        });
        continue;
      }
      const stringValue = guardFilterString(value);
      if (stringValue) {
        chips.push({
          id: stateKey,
          stateKey,
          source: 'filter',
          fieldLabel,
          valueLabel:
            stringValue === 'true'
              ? 'True'
              : stringValue === 'false'
                ? 'False'
                : stringValue,
          editorKind:
            stringValue === 'true' || stringValue === 'false'
              ? 'boolean'
              : 'text',
          isModifierEditable: false,
          disabled,
        });
      }
      continue;
    }

    // Fallback for any remaining string filter (e.g. free-text include terms).
    const stringValue = guardFilterString(value);
    if (stringValue) {
      chips.push({
        id: stateKey,
        stateKey,
        source: 'filter',
        fieldLabel: context.getFacetFieldLabel(stateKey),
        valueLabel: stringValue,
        editorKind: 'text',
        isModifierEditable: false,
        disabled,
      });
    }
  }

  for (const [stateKey, values] of Object.entries(searchFilters.facetValues)) {
    if (
      EXCLUDED_KEYS.has(stateKey) ||
      !Array.isArray(values) ||
      values.length === 0
    ) {
      continue;
    }
    const disabled = disabledFilters.has(stateKey);
    const valueLabel =
      values.length <= 3
        ? values
            .map((key) => context.formatFacetValue(stateKey, key))
            .join(', ')
        : `${values.length} selected`;
    chips.push({
      id: `facet:${stateKey}`,
      stateKey,
      source: 'facet',
      fieldLabel: context.getFacetFieldLabel(stateKey),
      valueLabel,
      editorKind: 'facetChecklist',
      isModifierEditable: false,
      disabled,
    });
  }

  return chips;
}
